// openEHR RM model — BMM package org.openehr.rm.data_types.time_specification
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import { DATA_VALUE } from "./basic.ts";
import type { DV_PARSABLE } from "./encapsulated.ts";
import { DV_DURATION } from "./quantity/date_time.ts";
import * as openehr_base from "../../base/mod.ts";
import { applyRmInit, type RmInit } from "../../base/apply_rm_init.ts";

/**
 * This is an abstract class of which all timing specifications are specialisations. Specifies points in time, possibly linked to the calendar, or a real world repeating event, such as  breakfast.
 */
export abstract class DV_TIME_SPECIFICATION extends DATA_VALUE {
  /**
   * The specification, in the HL7v3 syntax for \`PIVL\` or \`EIVL\` types.
   */
  value?: DV_PARSABLE;
  /**
   * Indicates what prototypical point in the calendar the specification is aligned to, e.g.  5th of the month . Empty if not aligned. Extracted from the  value' attribute.
   * @returns Result value
   */
  abstract calendar_alignment(): openehr_base.String;

  /**
   * Indicates what real-world event the specification is aligned to if any. Extracted from the  value' attribute.
   * @returns Result value
   */
  abstract event_alignment(): openehr_base.String;

  /**
   * Indicates if the specification is aligned with institution schedules, e.g. a hospital nursing changeover or meal serving times. Extracted from the  value' attribute.
   * @returns Result value
   */
  abstract institution_specified(): openehr_base.Boolean;
}

/**
 * Specifies periodic points in time, linked to the calendar (phase-linked), or a real world repeating event, such as  breakfast  (event-linked). Based on the HL7v3 data types \`PIVL<T>\` and \`EIVL<T>\`.
 *
 * Used in therapeutic prescriptions, expressed as \`INSTRUCTIONs\` in the openEHR model.
 */
export class DV_PERIODIC_TIME_SPECIFICATION extends DV_TIME_SPECIFICATION {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * HL7::TimingEvent codes for event-linked specifications (EIVL).
   * Ordered by length (longest first) for proper matching.
   */
  private static readonly EVENT_CODES = [
    "ACD", "ACM", "ACV", "AC",   // Before meals
    "PCD", "PCM", "PCV", "PC",   // After meals
    "WAKE", "HS",                 // Wake/sleep
    "CM", "CD", "CV", "C"        // Meal times
  ];

  /**
   * Pre-compiled regex patterns for efficient parsing.
   * These are compiled once as static properties to avoid repeated compilation.
   */
  private static readonly PERIOD_REGEX = /\/\(([^)]+)\)/;
  private static readonly ALIGNMENT_REGEX = /@([A-Z]{2})/;
  private static readonly DURATION_REGEX = /^(\d+)([a-zA-Z]+)$/;

  /**
   * The period of the repetition, computationally derived from the syntax representation. Extracted from the  value' attribute.
   * @returns Result value
   */
  period(): DV_DURATION {
    const valueStr = this.value?.value ?? "";
    
    // Extract period from PIVL format: [interval]/(period)@alignment
    // The period is the ISO 8601 duration between /( and )
    const periodMatch = valueStr.match(DV_PERIODIC_TIME_SPECIFICATION.PERIOD_REGEX);
    if (periodMatch) {
      const periodStr = periodMatch[1];
      // Convert shorthand notation to ISO 8601 if needed
      // e.g., "7d" -> "P7D", "1mo" -> "P1M"
      const isoDuration = this.convertToIsoDuration(periodStr);
      const result = new DV_DURATION();
      result.value = isoDuration;
      return result;
    }
    
    throw new Error("Cannot extract period from value: no period found in PIVL format");
  }

  /**
   * Convert shorthand duration notation to ISO 8601 format.
   * @param duration - Duration string (e.g., "7d", "1mo", "2h")
   * @returns ISO 8601 duration string (e.g., "P7D", "P1M", "PT2H")
   */
  private convertToIsoDuration(duration: string): string {
    // If already in ISO 8601 format, return as-is
    if (duration.startsWith("P")) {
      return duration;
    }
    
    // Match number and unit
    const match = duration.match(DV_PERIODIC_TIME_SPECIFICATION.DURATION_REGEX);
    if (!match) {
      // Return as-is if format not recognized
      return `P${duration.toUpperCase()}`;
    }
    
    const num = match[1];
    const unit = match[2].toLowerCase();
    
    // Map common abbreviations to ISO 8601
    const unitMap: Record<string, string> = {
      "s": `PT${num}S`,    // seconds
      "min": `PT${num}M`,  // minutes
      "h": `PT${num}H`,    // hours
      "d": `P${num}D`,     // days
      "w": `P${num}W`,     // weeks
      "wk": `P${num}W`,    // weeks
      "mo": `P${num}M`,    // months
      "m": `P${num}M`,     // months (when not time)
      "y": `P${num}Y`,     // years
      "a": `P${num}Y`,     // years (annum)
    };
    
    return unitMap[unit] || `P${duration.toUpperCase()}`;
  }

  /**
   * Valid calendar alignment codes from HL7::CalendarCycle domain.
   */
  private static readonly VALID_ALIGNMENTS = ["DW", "DM", "DY", "WY", "MY", "HD"];

  /**
   * Calendar alignment extracted from value.
   * Returns alignment code from HL7::CalendarCycle domain (DW, DM, DY, WY, MY, HD).
   * @returns Result value
   */
  calendar_alignment(): openehr_base.String {
    const valueStr = this.value?.value ?? "";
    
    // Extract alignment from PIVL format: ...@alignment[IST]
    // Using pre-compiled static regex for efficiency
    const alignMatch = valueStr.match(DV_PERIODIC_TIME_SPECIFICATION.ALIGNMENT_REGEX);
    if (alignMatch) {
      const alignment = alignMatch[1];
      // Validate it's a known calendar alignment code
      if (DV_PERIODIC_TIME_SPECIFICATION.VALID_ALIGNMENTS.includes(alignment)) {
        return openehr_base.String.from(alignment);
      }
    }
    
    return openehr_base.String.from("");
  }

  /**
   * Event alignment extracted from value.
   * Returns event code from HL7::TimingEvent domain for EIVL specifications.
   * @returns Result value
   */
  event_alignment(): openehr_base.String {
    const valueStr = this.value?.value ?? "";
    const formalism = this.value?.formalism ?? "";
    
    // Only EIVL has event alignment
    if (formalism !== "HL7:EIVL") {
      return openehr_base.String.from("");
    }
    
    // Look for event codes at the start (ordered by length for proper matching)
    for (const code of DV_PERIODIC_TIME_SPECIFICATION.EVENT_CODES) {
      if (valueStr.startsWith(code)) {
        return openehr_base.String.from(code);
      }
    }
    
    return openehr_base.String.from("");
  }

  /**
   * Indicates if the specification is aligned with institution schedules.
   * Returns true if "IST" suffix is present in the value.
   * @returns Result value
   */
  institution_specified(): openehr_base.Boolean {
    const valueStr = this.value?.value ?? "";
    return openehr_base.Boolean.from(valueStr.includes("IST"));
  }
}

/**
 * Specifies points in time in a general syntax. Based on the HL7v3 GTS data type.
 */
export class DV_GENERAL_TIME_SPECIFICATION extends DV_TIME_SPECIFICATION {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * HL7::TimingEvent codes for event-linked specifications.
   * Ordered by length (longest first) for proper matching.
   */
  private static readonly EVENT_CODES = [
    "ACD", "ACM", "ACV", "AC",   // Before meals
    "PCD", "PCM", "PCV", "PC",   // After meals
    "WAKE", "HS",                 // Wake/sleep
    "CM", "CD", "CV", "C"        // Meal times
  ];

  /**
   * Pre-compiled regex patterns for efficient parsing.
   */
  private static readonly ALIGNMENT_REGEX = /@([A-Z]{2})/;
  private static readonly VALID_ALIGNMENTS = ["DW", "DM", "DY", "WY", "MY", "HD"];
  
  /**
   * Pre-compiled regex patterns for event code matching in GTS expressions.
   * Each pattern matches the event code at start or after GTS operators.
   */
  private static readonly EVENT_REGEXES = DV_GENERAL_TIME_SPECIFICATION.EVENT_CODES.map(
    code => new RegExp(`(^|[;\\\\(])${code}([^A-Z]|$)`)
  );

  /**
   * Calendar alignment extracted from value.
   * For GTS format, returns the alignment from the first PIVL component if present.
   * @returns Result value
   */
  calendar_alignment(): openehr_base.String {
    const valueStr = this.value?.value ?? "";
    
    // GTS may contain PIVL components - look for @alignment pattern
    // Using pre-compiled static regex for efficiency
    const alignMatch = valueStr.match(DV_GENERAL_TIME_SPECIFICATION.ALIGNMENT_REGEX);
    if (alignMatch) {
      const alignment = alignMatch[1];
      // Validate it's a known calendar alignment code
      if (DV_GENERAL_TIME_SPECIFICATION.VALID_ALIGNMENTS.includes(alignment)) {
        return openehr_base.String.from(alignment);
      }
    }
    
    return openehr_base.String.from("");
  }

  /**
   * Event alignment extracted from value.
   * For GTS format, returns the event from the first EIVL component if present.
   * @returns Result value
   */
  event_alignment(): openehr_base.String {
    const valueStr = this.value?.value ?? "";
    
    // Look for event codes at word boundaries in the GTS expression
    // Using pre-compiled regex patterns for efficiency
    for (let i = 0; i < DV_GENERAL_TIME_SPECIFICATION.EVENT_CODES.length; i++) {
      if (DV_GENERAL_TIME_SPECIFICATION.EVENT_REGEXES[i].test(valueStr)) {
        return openehr_base.String.from(DV_GENERAL_TIME_SPECIFICATION.EVENT_CODES[i]);
      }
    }
    
    return openehr_base.String.from("");
  }

  /**
   * Returns true if any part of the specification is institution specified.
   * Checks for "IST" anywhere in the GTS expression.
   * @returns Result value
   */
  institution_specified(): openehr_base.Boolean {
    const valueStr = this.value?.value ?? "";
    return openehr_base.Boolean.from(valueStr.includes("IST"));
  }
}
